import { getUrlParts, isSodimacRetail, isStandalone, isWhitelabel } from '@digital-retail/store-manager';
import { isWindow } from '../../utils/deviceChecker';

const DIRECT_HOST_REGEXES = [/.sodimac.com.mx/];

export const getStoreUrl = (host, context) => {
  if (DIRECT_HOST_REGEXES.some((regex) => regex.test(host))) {
    return host;
  }

  if (context !== 'CART') {
    if (/.tottus.cl|.tottus.com/.test(host)) {
      return host.replace(/.tottus.cl|.tottus.com/, '.falabella.com');
    }
  }

  const stores = ['sodimac', 'linio', 'tienda', 'homecenter'];
  if (context !== 'CART' || /tottus.falabella/.test(host)) {
    stores.push('tottus');
  }

  const storeFilter = stores.filter((store) => host.includes(store));

  if (/.sodimac.cl|.sodimac.com/.test(host)) {
    return host.replace(/.sodimac.cl|.sodimac.com/, '.falabella.com');
  }

  if (storeFilter.length > 0) {
    return host.replace(`-${storeFilter}`, '').replace(`${storeFilter}`, 'www');
  }
  return host;
};

const IMG_DOMAIN_REPLACE_REGEX =
  /https:\/\/((staging-)?media\.falabella\.com|imagedelivery\.net\/4fYuQyy-r8_rpBpcY7lH_A)/;

export default {
  getProductsUrl({ base, siteId, params }) {
    return `${base}/v1/search/${siteId}?${Object.keys(params)
      .map((key) => `${key}=${params[key]}`)
      .join('&')}`;
  },
  getShopUrl({ base, siteId, params }) {
    return `${base}/v1/shop/${siteId}?${Object.keys(params)
      .map((key) => `${key}=${params[key]}`)
      .join('&')}`;
  },
  getListingsUrl({ base, siteId, rest }) {
    return `${base}/v1/listing/${siteId}?${Object.keys(rest)
      .map((key) => `${key}=${rest[key]}`)
      .join('&')}`;
  },
  getInterlinkingUrl({ base, pageType, rest }) {
    return `${base}/v1/links/page/${pageType}?${Object.keys(rest)
      .map((key) => `${key}=${rest[key]}`)
      .join('&')}`;
  },
  getCollectionUrl({ base, siteId, rest }) {
    return `${base}/v1/collection/${siteId}?${Object.keys(rest)
      .map((key) => `${key}=${rest[key]}`)
      .join('&')}`;
  },
  getCommerceAnalyticsInfoUrl: (regionCode) => {
    const host = isWindow() ? window.location.origin : getUrlParts({ regionCode }).origin;
    return `${host}/s/account/v1/analytics-info`;
  },
  getLocationsUrl: ({ base, siteId }) => {
    return `${base}/v1/zones/${siteId}`;
  },
  getImagesURL: (regionCode) => {
    let fa = 'Falabella';
    switch (regionCode) {
      case 'co': {
        fa = 'FalabellaCO';
        break;
      }
      case 'pe': {
        fa = 'FalabellaPE';
        break;
      }
      default: {
        fa = 'Falabella';
      }
    }
    return `https://falabella.scene7.com/is/image/${fa}`;
  },
  getCFImagesURL: (regionCode, store) => {
    const imageUrls = {
      cl: 'https://media.falabella.com/FalabellaCL',
      pe: 'https://media.falabella.com/FalabellaPEAndCO',
      co: 'https://media.falabella.com/FalabellaPEAndCO',
    };

    const image = imageUrls[regionCode] || imageUrls.cl;
    const { metadata } = getUrlParts({ regionCode, store });

    return image.replace(IMG_DOMAIN_REPLACE_REGEX, metadata.imgDomain);
  },
  getCanonicalUrl: ({ regionCode, searchTerm, store, isShopPage, shopPageId }) => {
    const isWhiteLabelExperience = isWhitelabel({ store });
    const { href } = getUrlParts({ regionCode, store, isWhiteLabelExperience });

    if (isShopPage) {
      return `${href}/${isStandalone({ store }) ? 'b' : 'shop'}/${shopPageId}`;
    }
    return `${href}/${isStandalone({ store }) ? 'buscar' : 'search'}?Ntt=${searchTerm}`;
  },
  getFacetValues({ base, siteId, params }) {
    return `${base}/v1/facet/${siteId}?${Object.keys(params)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&')}`;
  },
  getSelectedFacetValues({ base, siteId, params }) {
    return `${base}/v1/facet/${siteId}?${params}`;
  },
  getCartCount: (cartId, regionCode, politicalId, latLong) => {
    const host = isWindow() ? window.location.origin : getUrlParts({ regionCode }).origin;
    const hostUrl = getStoreUrl(host, 'CART');
    let url = `${hostUrl}/s/checkout/v1/carts/${cartId}/count?politicalAreaId=${politicalId}`;
    if (latLong?.latitude && latLong?.longitude) {
      url += `&lat=${latLong?.latitude}&long=${latLong?.longitude}`;
    }
    return url;
  },
  addToCartCommerceUrl: (query, regionCode) => {
    const host = isWindow() ? window.location.origin : getUrlParts({ regionCode }).origin;
    const hostUrl = getStoreUrl(host, 'CART');
    return `${hostUrl}/s/checkout/v1/carts${query}`;
  },
  addToListCommerceUrl: (regionCode) => {
    const host = isWindow() ? window.location.origin : getUrlParts({ regionCode }).origin;
    const hostUrl = getStoreUrl(host, 'CART');
    return `${hostUrl}/s/checkout/v1/lists`;
  },
  getAllListsCommerceUrl: (listType, regionCode, politicalId) => {
    const host = isWindow() ? window.location.origin : getUrlParts({ regionCode }).origin;
    const hostUrl = getStoreUrl(host, 'CART');
    const url = new URL(`${hostUrl}/s/checkout/v2/lists?listType=${listType}&politicalAreaId=${politicalId}`);
    url.searchParams.set('includeLines', 'true');
    return url;
  },
  deleteFromListCommerceUrl: (listId, listLineItemId, regionCode) => {
    const host = isWindow() ? window.location.origin : getUrlParts({ regionCode }).origin;
    const hostUrl = getStoreUrl(host, 'CART');
    return `${hostUrl}/s/checkout/v1/lists/${listId}/list-lines/${listLineItemId}`;
  },
  patchCartlineCommerceUrl: (cartId, regionCode) => {
    const host = isWindow() ? window.location.origin : getUrlParts({ regionCode }).origin;
    const hostUrl = getStoreUrl(host, 'CART');
    return `${hostUrl}/s/checkout/v1/carts/${cartId}/cart-lines`;
  },
  removeCartlineCommerceUrl: (cartId, regionCode) => {
    const host = isWindow() ? window.location.origin : getUrlParts({ regionCode }).origin;
    return `${host}/s/checkout/v1/carts/${cartId}/remove-items`;
  },
  deleteCartlineCommerceUrl: (cartId, cartLineItemId, regionCode) => {
    const host = isWindow() ? window.location.origin : getUrlParts({ regionCode }).origin;
    return `${host}/s/checkout/v1/carts/${cartId}/cart-lines/${cartLineItemId}`;
  },
  getBrandApiURL({ base, siteId, params }) {
    return `${base}/v1/brand/${siteId}?${Object.keys(params)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&')}`;
  },
  getSellerApiURL({ base, siteId, params }) {
    return `${base}/v1/seller/${siteId}?${Object.keys(params)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&')}`;
  },
  formatRelativeUrl: (url, regionCode) => {
    const host = getUrlParts({ regionCode }).origin;
    return `${host}${url}`;
  },
  getEnvironmentSpecificUrl: (url, env) => {
    return url.replace('www', env);
  },
  getProductLiteDetailsUrl({ regionCode, rest }) {
    const host = isWindow() ? window.location.origin : getUrlParts({ regionCode }).origin;
    const hostUrl = getStoreUrl(host);
    return `${hostUrl}/s/browse/v1/product/${regionCode}/lite?${Object.keys(rest)
      .map((key) => `${key}=${rest[key]}`)
      .join('&')}`;
  },
  getGeoUrl: ({ base, siteId, store }) => {
    const version = isSodimacRetail(store) ? 'v3' : 'v2';
    return `${base}/${version}/states/${siteId}`;
  },
  mabayaVisitUrl: ({ storeHash, sku, referrerSignal, referrerURL, referrerType, cookie }) => {
    const url = new URL('https://us.evt.mb-srv.com/v2/server2server/visit');
    const params = {
      storeHash,
      sku,
      referrerSignal,
      referrerURL,
      ReferrerType: referrerType,
    };
    if (cookie) params.cookie = cookie;
    url.search = new URLSearchParams(params).toString();

    return url.toString();
  },
  mabayaClickUrl: ({ storeHash, platform, adInfo, mPos, cookie }) => {
    return `https://us.widget.mb-srv.com/v2/server2server/click?storeHash=${storeHash}&cookie=${cookie}&platform=${platform}&mabaya_ad_info=${adInfo}&mPos=${mPos}`;
  },
  mabayaImpressionUrl: ({ storeHash, platform, adInfo, mPos }) => {
    return `https://us.widget.mb-srv.com/v2/server2server/imp?storeHash=${storeHash}&platform=${platform}&mabaya_ad_info=${adInfo}&mPos=${mPos}`;
  },
  getDistrictInfo: ({ base, siteId, politicalId, latLong, getAllZones = false, store }) => {
    const version = isSodimacRetail(store) ? 'v3' : 'v2';
    let url = `${base}/${version}/districts/${siteId}?politicalId=${politicalId}&getAllZones=${getAllZones}`;
    if (latLong?.latitude && latLong?.longitude) {
      url += `&latitude=${latLong?.latitude}&longitude=${latLong?.longitude}`;
    }
    return url;
  },
  getVirtualZonesUrl: ({ base, politicalId, regionCode }) => {
    return `${base}/v1/geo/${regionCode || 'mx'}/virtual-zones?politicalAreaId=${politicalId}`;
  },
  getLowestPlanUrl: ({ regionCode, environment, rest }) => {
    const host = getUrlParts({ regionCode, environment }).origin;
    return `${host}/s/browse/v1/search/${regionCode}?${Object.keys(rest)
      .map((key) => `${key}=${rest[key]}`)
      .join('&')}`;
  },
  sponsoredClickUrl: (baseUrl) => `${baseUrl}/v1/vas/click`,
  sponsoredImpressionUrl: (baseUrl) => `${baseUrl}/v1/vas/impression`,
  sponsoredVisitUrl: (baseUrl) => `${baseUrl}/v1/vas/visit`,
  getWidgets: ({
    channel,
    zones,
    regionCode,
    userId,
    slots,
    site,
    contextId,
    politicalId,
    priceGroupId,
    pageType,
    pageId,
    bypassCache,
  }) => {
    const host = isWindow() ? window.location.origin : getUrlParts({ regionCode }).origin;
    const hostUrl = getStoreUrl(host);
    const url = new URL(
      `${
        hostUrl.includes('localhost') ? 'https://www.falabella.com' : hostUrl
      }/s/browse/v2/recommended-products/${regionCode}`
    );
    if (channel !== undefined) url.searchParams.set('channel', channel);
    if (contextId !== undefined) url.searchParams.set('contextId', contextId);
    if (contextId !== undefined) url.searchParams.set('contextId', contextId);
    if (userId !== undefined) url.searchParams.set('userId', userId);
    if (slots !== undefined) url.searchParams.set('slots', slots);
    if (site !== undefined) url.searchParams.set('site', site);
    if (priceGroupId !== undefined) url.searchParams.set('priceGroupId', priceGroupId);
    if (politicalId !== undefined) url.searchParams.set('politicalid', politicalId);
    if (zones !== undefined) url.searchParams.set('zones', zones);
    if (pageType !== undefined) url.searchParams.set('pageType', pageType);
    if (pageId !== undefined) url.searchParams.set('pageId', pageId);
    if (bypassCache !== undefined) url.searchParams.set('bypass_cache', bypassCache);
    return url.toString();
  },
};
